import React from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    IconButton,
    styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Same visual language as the existing WelcomeModal — dark Vtrender style.
const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
        backgroundColor: "rgb(13, 32, 63)",
        color: "#ffffff",
        borderRadius: "12px",
        width: "100%",
        maxWidth: "600px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        [theme.breakpoints.down("sm")]: {
            width: "90%",
            margin: 0,
        },
    },
}));

/**
 * PopupShell
 *
 * Common structural chrome shared by every popup type:
 * Dialog + backdrop, close button, title area, scrollable content
 * area, and a footer/action area.
 *
 * This component has NO popup-specific business logic. It just lays
 * out whatever it's given. Each popup template (InfoPopup, future
 * ActionPopup, etc.) decides what goes in each slot.
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - title: string — optional
 * - topSlot: ReactNode — optional, rendered above the title (e.g. image, countdown timer)
 * - children: ReactNode — main content area (usually <PopupMarkdown />)
 * - footer: ReactNode — optional, rendered in the footer/action area (usually buttons)
 * - maxContentHeight: string — cap on content area height before it scrolls
 */
const PopupShell = ({
    open,
    onClose,
    title,
    topSlot,
    children,
    footer,
    maxContentHeight = "60vh",
}) => {
    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            aria-labelledby="popup-shell-title"
        >
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 12,
                    top: 12,
                    color: "rgba(255, 255, 255, 0.7)",
                    zIndex: 1,
                    "&:hover": {
                        color: "#ffffff",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 3 }}>
                {topSlot}

                {title && (
                    <Typography
                        id="popup-shell-title"
                        variant="h5"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            pr: 4,
                            wordBreak: "break-word",
                        }}
                    >
                        {title}
                    </Typography>
                )}

                <Box
                    sx={{
                        maxHeight: maxContentHeight,
                        overflowY: "auto",
                        pr: 0.5,
                        // subtle scrollbar so it fits the dark theme
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            borderRadius: "3px",
                        },
                    }}
                >
                    {children}
                </Box>

                {footer && (
                    <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
                        {footer}
                    </Box>
                )}
            </DialogContent>
        </StyledDialog>
    );
};

export default PopupShell;
